import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

/**
 * Country interface with title and icon
 */
export interface Country {
    title: string;
    icon: string;
}

/**
 * Country flags mapping (centralized).
 * Sorted: Global first, then all others alphabetically by title.
 */
const COUNTRY_DATA_RAW: Country[] = [
    { title: 'Global', icon: '🌍' },
    { title: 'United States', icon: '🇺🇸' },
    { title: 'United Kingdom', icon: '🇬🇧' },
    { title: 'Germany', icon: '🇩🇪' },
    { title: 'France', icon: '🇫🇷' },
    { title: 'India', icon: '🇮🇳' },
    { title: 'Australia', icon: '🇦🇺' },
    { title: 'Canada', icon: '🇨🇦' },
    { title: 'Japan', icon: '🇯🇵' },
    { title: 'China', icon: '🇨🇳' },
    { title: 'Brazil', icon: '🇧🇷' },
    { title: 'Italy', icon: '🇮🇹' },
    { title: 'Spain', icon: '🇪🇸' },
    { title: 'Netherlands', icon: '🇳🇱' },
    { title: 'Sweden', icon: '🇸🇪' },
    { title: 'Norway', icon: '🇳🇴' },
    { title: 'Denmark', icon: '🇩🇰' },
    { title: 'Finland', icon: '🇫🇮' },
    { title: 'Switzerland', icon: '🇨🇭' },
    { title: 'Austria', icon: '🇦🇹' },
    { title: 'Belgium', icon: '🇧🇪' },
    { title: 'Poland', icon: '🇵🇱' },
    { title: 'Czech Republic', icon: '🇨🇿' },
    { title: 'South Korea', icon: '🇰🇷' },
    { title: 'Singapore', icon: '🇸🇬' },
    { title: 'Hong Kong', icon: '🇭🇰' },
    { title: 'New Zealand', icon: '🇳🇿' },
    { title: 'South Africa', icon: '🇿🇦' },
    { title: 'Mexico', icon: '🇲🇽' },
    { title: 'Argentina', icon: '🇦🇷' },
    { title: 'Estonia', icon: '🇪🇪' },
    { title: 'Israel', icon: '🇮🇱' },
    { title: 'UAE', icon: '🇦🇪' },
    { title: 'Saudi Arabia', icon: '🇸🇦' },
    { title: 'Turkey', icon: '🇹🇷' },
    { title: 'Portugal', icon: '🇵🇹' },
    { title: 'Ireland', icon: '🇮🇪' },
    { title: 'Indonesia', icon: '🇮🇩' },
    { title: 'Thailand', icon: '🇹🇭' },
    { title: 'Vietnam', icon: '🇻🇳' },
    { title: 'Malaysia', icon: '🇲🇾' },
    { title: 'Philippines', icon: '🇵🇭' },
    { title: 'Greece', icon: '🇬🇷' }
];

const COUNTRY_DATA: Country[] = [
    COUNTRY_DATA_RAW[0],
    ...COUNTRY_DATA_RAW.slice(1).sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }))
];

/**
 * Default fallback countries list (for backward compatibility)
 */
export const DEFAULT_COUNTRIES: string[] = COUNTRY_DATA.map(c => c.title);

@Injectable({
    providedIn: 'root'
})
export class CountryService {
    constructor() { }

    /**
     * Get country data with icons from centralized mapping
     * Returns countries with both title and icon
     */
    getCountriesWithIcons(): Country[] {
        return COUNTRY_DATA;
    }

    /**
     * Get country icon by title
     * Returns the icon for a given country title, or default icon if not found
     */
    getCountryIcon(countryTitle: string): string {
        const country = COUNTRY_DATA.find(c => 
            c.title.toLowerCase() === countryTitle?.toLowerCase()
        );
        return country?.icon || '🌐';
    }

    /**
     * Get country data by title
     * Returns the full Country object for a given title
     */
    getCountryByTitle(countryTitle: string): Country | undefined {
        return COUNTRY_DATA.find(c => 
            c.title.toLowerCase() === countryTitle?.toLowerCase()
        );
    }

    /**
     * Get country options from API config or fallback to default list
     * Ensures 'Global' is always included as the first option
     * Returns array of country titles (for backward compatibility)
     */
    getCountryOptions(): Observable<string[]> {
        return of(this.normalizeCountries(DEFAULT_COUNTRIES));
    }

    /**
     * Get country options with icons from API config or fallback to default list
     * Returns array of Country objects with title and icon
     */
    getCountryOptionsWithIcons(): Observable<Country[]> {
        const normalizedTitles = this.normalizeCountries(DEFAULT_COUNTRIES);
        return of(
            normalizedTitles.map(title => {
                const country = COUNTRY_DATA.find(c =>
                    c.title.toLowerCase() === title.toLowerCase()
                );
                return country || { title, icon: '🌐' };
            })
        );
    }

    /**
     * Get country options synchronously (from cache if available)
     * Returns array of country titles (for backward compatibility)
     */
    getCountryOptionsSync(): string[] {
        return this.normalizeCountries(DEFAULT_COUNTRIES);
    }

    /**
     * Get country options with icons synchronously (from cache if available)
     * Returns array of Country objects with title and icon
     */
    getCountryOptionsWithIconsSync(): Country[] {
        const normalizedTitles = this.normalizeCountries(DEFAULT_COUNTRIES);
        return normalizedTitles.map(title => {
            const country = COUNTRY_DATA.find(c => 
                c.title.toLowerCase() === title.toLowerCase()
            );
            return country || { title, icon: '🌐' };
        });
    }

    /**
     * Get default countries list (fallback)
     * Returns array of country titles (for backward compatibility)
     */
    getDefaultCountries(): string[] {
        return this.normalizeCountries(DEFAULT_COUNTRIES);
    }

    /**
     * Get default countries with icons (fallback)
     * Returns array of Country objects with title and icon
     */
    getDefaultCountriesWithIcons(): Country[] {
        const normalizedTitles = this.normalizeCountries(DEFAULT_COUNTRIES);
        return normalizedTitles.map(title => {
            const country = COUNTRY_DATA.find(c => 
                c.title.toLowerCase() === title.toLowerCase()
            );
            return country || { title, icon: '🌐' };
        });
    }

    /**
     * Country alias mapping - maps aliases to canonical names
     */
    private countryAliasMap: { [key: string]: string } = {
        'usa': 'United States',
        'us': 'United States',
        'united states of america': 'United States',
        'uk': 'United Kingdom',
        'great britain': 'United Kingdom',
        'britain': 'United Kingdom',
        'england': 'United Kingdom',
        'uae': 'UAE', // Keep as is since it's already in COUNTRY_DATA
        'united arab emirates': 'UAE'
    };

    /**
     * Normalize country name - convert aliases to canonical names
     */
    private normalizeCountryName(country: string): string {
        const normalized = country?.trim();
        if (!normalized) return country;
        
        const lowerKey = normalized.toLowerCase();
        const canonical = this.countryAliasMap[lowerKey];
        
        // If alias found, return canonical name; otherwise return original (preserving case)
        return canonical || normalized;
    }

    /**
     * Ensure 'Global' is first, values are trimmed, duplicates (case-insensitive) are removed,
     * and aliases are normalized to canonical names
     */
    private normalizeCountries(input: string[]): string[] {
        const cleaned = input
            .map(country => this.normalizeCountryName(country))
            .filter((country): country is string => Boolean(country));
        
        const unique: string[] = [];
        const seen = new Set<string>();
        for (const country of cleaned) {
            const key = country.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                unique.push(country);
            }
        }
        
        const globalEntry = unique.find(country => country.toLowerCase() === 'global') || 'Global';
        const others = unique.filter(country => country.toLowerCase() !== 'global');
        others.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        return [globalEntry, ...others];
    }
}

